import { useId, type ReactNode } from 'react';
import {
  ACCESS_END_QUICK_CHOICES,
  accessEndYmd,
  maxBuyerAccessEndYmd,
  type AccessEndChoice,
  type AccessEndValue,
} from '../lib/access-end';
import { formatDateOnly, todayYmd } from '../lib/date-only';
import { LegendWithAside } from './legend-aside';

/**
 * "How long does this access last?" — quick choices (1 week, 2 weeks, 1 month,
 * 2 months) and "Pick a date…" (never later than 2 months out for a buyer). There is no "Ongoing" unless the caller allows it,
 * which is only for the seller's side and the intermediary's own team; buyer and
 * buyer-team access always ends (owner rule 2026-10-21). Shows the date it ends.
 * Controlled: the parent holds the {@link AccessEndValue} and sends
 * `accessEndIso(value)` as `expiresAt`.
 */
export function AccessEndPicker({
  value,
  onChange,
  allowOngoing = false,
  disabled = false,
  legend = 'Access ends',
  legendAside,
}: {
  value: AccessEndValue;
  onChange: (next: AccessEndValue) => void;
  allowOngoing?: boolean;
  disabled?: boolean;
  legend?: string;
  /** A "?" help tip shown after the legend (outside it, so the group name is unchanged). */
  legendAside?: ReactNode;
}) {
  const name = useId();
  const today = todayYmd();
  const choose = (choice: AccessEndChoice) => onChange({ ...value, choice });
  // The seller's and intermediary's own people (`allowOngoing`) have no maximum.
  const capped = !allowOngoing;
  const ymd = accessEndYmd(value, today, capped);
  const options: { id: AccessEndChoice; label: string }[] = [
    ...ACCESS_END_QUICK_CHOICES,
    { id: 'date', label: 'Pick a date…' },
    ...(allowOngoing ? [{ id: 'ongoing' as const, label: 'Ongoing (no end date)' }] : []),
  ];
  return (
    <fieldset className="space-y-2" disabled={disabled} data-testid="access-end-picker">
      <LegendWithAside className="text-sm font-medium" aside={legendAside}>
        {legend}
      </LegendWithAside>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {options.map((o) => (
          <label key={o.id} className="flex items-center gap-1.5 text-sm">
            <input
              type="radio"
              name={name}
              className="h-4 w-4"
              checked={value.choice === o.id}
              onChange={() => choose(o.id)}
            />
            {o.label}
          </label>
        ))}
      </div>
      {value.choice === 'date' ? (
        <input
          type="date"
          aria-label="End date"
          min={today}
          max={capped ? maxBuyerAccessEndYmd(today) : undefined}
          value={value.date}
          onChange={(e) => onChange({ choice: 'date', date: e.target.value })}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        />
      ) : null}
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {value.choice === 'ongoing'
          ? 'Ongoing until you end it.'
          : ymd
            ? `Until ${formatDateOnly(ymd)}`
            : capped
              ? 'Choose the last day of access, up to 2 months from today.'
              : 'Choose the last day of access.'}
      </p>
    </fieldset>
  );
}
